import React, { useState, useEffect } from 'react'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import Chip from '@material-ui/core/Chip'
import Link from '@material-ui/core/Link'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import ThumbUpAltIcon from "@material-ui/icons/ThumbUpAlt";
import ThumbUpAltOutlined from '@material-ui/icons/ThumbUpAltOutlined';
import RoomOutlinedIcon from '@material-ui/icons/RoomOutlined';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { useHistory, Link as RouterLink } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RESET_COURSE } from '../../../constants/actionTypes'

import { likeCourse, submitCourseUpdate, rejectCourseUpdate, getCourseById } from '../../../actions/courses'
import CompareUpdate from './CompareUpdate'
import useStyles from '../styles'
import { createCourseSlug } from '../../../helper'

const Course = ({ course, featuredCourse, pending }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const history = useHistory();
    const user = JSON.parse(localStorage.getItem("profile"));
    const [likes, setLikes] = useState(course?.likes || []);
    const [compareOpen, setCompareOpen] = useState(false);
    const [originalCourse, setOriginalCourse] = useState(null);
    const [shouldOpenCompare, setShouldOpenCompare] = useState(false);
    const [confirmationOpen, setConfirmationOpen] = useState(false);
    const [confirmationAction, setConfirmationAction] = useState(null);
    const { course: courseData } = useSelector((state) => state.courses);

    useEffect(() => {
        if (shouldOpenCompare && courseData && courseData._id) {
            setCompareOpen(true);
            setShouldOpenCompare(false);
        }
    }, [courseData, shouldOpenCompare]);

    const getAnonymousId = () => {
        let anonId = localStorage.getItem('anonymousId');
        if (!anonId) {
            anonId = 'anon_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
            localStorage.setItem('anonymousId', anonId);
        }
        return anonId;
    };

    const userId = user?.result?.sub || user?.result?._id || getAnonymousId();
    const hasLikedCourse = likes.find((like) => like === userId);

    const handleLike = async () => {
        dispatch(likeCourse(course._id, userId));
        if (hasLikedCourse) {
            setLikes(likes.filter((id) => id !== userId));
        } else {
            setLikes([...likes, userId]);
        }
    };

    const courseNav = () => {
        dispatch({ type: RESET_COURSE, payload: [] })
        history.push(`/course/${createCourseSlug(course.name, course._id)}`)
    }

    const compareUpdate = async () => {
        setShouldOpenCompare(true);
        dispatch(getCourseById(course._id))
    }

    const submitUpdate = () => {
        setConfirmationAction('update');
        setConfirmationOpen(true);
    }

    const rejectUpdate = () => {
        setConfirmationAction('reject');
        setConfirmationOpen(true);
    }

    const handleConfirmAction = () => {
        setConfirmationOpen(false);
        if (confirmationAction === 'update') {
            dispatch(submitCourseUpdate(course));
        } else if (confirmationAction === 'reject') {
            dispatch(rejectCourseUpdate(course._id));
        }
        setConfirmationAction(null);
    }

    const Likes = () => {
        if (likes.length > 0) {
            return likes.find((like) => like === userId) ? (
                <>
                    <ThumbUpAltIcon fontSize="small" />
                    &nbsp;
                    {likes.length > 2
                        ? `You and ${likes.length - 1} others`
                        : `${likes.length} like${likes.length > 1 ? "s" : ""}`}
                </>
            ) : (
                <>
                    <ThumbUpAltOutlined fontSize="small" />
                    &nbsp;{course.likes.length} {course.likes.length === 1 ? "Like" : "Likes"}
                </>
            );
        }
        return (
            <>
                <ThumbUpAltOutlined fontSize="small" />
                &nbsp;Like
            </>
        );
    };

    return (
        <>
            <Paper
                className={`${classes.courseCard} ${featuredCourse ? classes.courseCardFeatured : ''}`}
                elevation={0}
            >
                {featuredCourse &&
                    <Chip size='small' label='Featured Course' className={classes.featuredBadge} />}
                <div className={classes.courseCardBody}>
                    <div className={classes.courseCardInfo}>
                        <Typography
                            variant="h6"
                            component="h2"
                            color="textPrimary"
                            className={classes.courseCardName}
                        >
                            <Link
                                component={RouterLink}
                                aria-label={`link to more information on '${course.name}'`}
                                className={classes.courseLink}
                                onClick={() => dispatch({ type: RESET_COURSE, payload: [] })}
                                to={`/course/${createCourseSlug(course.name, course._id)}`}>
                                {course.name}
                            </Link>
                        </Typography>
                        <Typography component="div" className={classes.courseCardLocation}>
                            <RoomOutlinedIcon style={{ fontSize: '1rem', flexShrink: 0 }} />
                            <span>
                                {[
                                    course.address,
                                    [course.city, course.state].filter(Boolean).join(', ') + (course.zip ? ` ${course.zip}` : ''),
                                    course.country,
                                ].filter((part) => part && part.trim()).join(' · ')}
                            </span>
                        </Typography>
                        {course.website && (
                            <Typography component="div" className={classes.courseCardLocation} style={{ marginTop: 4 }}>
                                <Link
                                    tabIndex='0'
                                    className={classes.courseLink}
                                    style={{ fontSize: '0.85rem' }}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    href={`${course.website.includes("http")
                                        ? course.website
                                        : "https://" + course.website
                                        }`}
                                >
                                    {course.website}
                                </Link>
                            </Typography>
                        )}
                        {(course.holes || course.slopeRating || course.courseRating) && (
                            <div className={classes.courseCardChips}>
                                {course.holes && <Chip size="small" label={`${course.holes} Holes`} />}
                                {course.courseRating && <Chip size="small" variant="outlined" label={`Rating ${course.courseRating}`} />}
                                {course.slopeRating && <Chip size="small" variant="outlined" label={`Slope ${course.slopeRating}`} />}
                            </div>
                        )}
                    </div>
                    <div className={pending ? classes.courseAddressChild3Admin : classes.courseCardActions}>
                        {pending && <Button
                            className={classes.iconHandler}
                            size="small"
                            disabled={!user?.result}
                            onClick={compareUpdate}
                        >
                            Compare
                        </Button>}
                        <Button
                            className={classes.iconHandler}
                            size="small"
                            disabled={pending ? !user?.result : false}
                            onClick={pending ? submitUpdate : handleLike}
                        >
                            {pending ? 'Update' : <Likes />}
                        </Button>
                        {pending && <Button
                            className={classes.iconCautionHandler}
                            variant='contained'
                            size="small"
                            disabled={!user?.result}
                            onClick={rejectUpdate}
                        >
                            Reject
                        </Button>}
                        {!pending && (
                            <Button
                                size="small"
                                color="primary"
                                endIcon={<ArrowForwardIcon style={{ fontSize: '0.95rem' }} />}
                                onClick={() => courseNav()}
                                aria-label={`view details for '${course.name}'`}
                            >
                                View
                            </Button>
                        )}
                    </div>
                </div>
            </Paper>
            <CompareUpdate
                open={compareOpen}
                onClose={() => setCompareOpen(false)}
                originalCourse={courseData}
                pendingCourse={course}
            />
            <Dialog
                open={confirmationOpen}
                onClose={() => {
                    setConfirmationOpen(false);
                    setConfirmationAction(null);
                }}
            >
                <DialogTitle>
                    {confirmationAction === 'update' ? 'Confirm Update' : 'Confirm Rejection'}
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        {confirmationAction === 'update'
                            ? `Are you sure you want to update "${course.name}"?`
                            : `Are you sure you want to reject the update for "${course.name}"?`}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setConfirmationOpen(false);
                            setConfirmationAction(null);
                        }}
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmAction}
                        color={confirmationAction === 'reject' ? 'secondary' : 'primary'}
                        variant="contained"
                    >
                        {confirmationAction === 'update' ? 'Update' : 'Reject'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default Course